import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Alert, Button, Select, Space, Tag, Tooltip, theme } from 'antd'
import { EyeOutlined, LogoutOutlined, SafetyCertificateOutlined } from '@ant-design/icons'
import { useAuth } from '@/contexts/AuthContext'
import { useSimulazione } from '@/contexts/SimulazioneContext'
import { registraPercorso } from '@/lib/raccoltaPermessi'
import { PannelloPermessi } from '@/components/PannelloPermessi'

/**
 * SimulazioneRuolo — selettore "vedi come <ruolo>" e fascia di avviso.
 *
 * Montato dentro AuthProvider in App.tsx, non nei layout: i tre layout hanno
 * header separati e le rotte /tablet/* non passano da LayoutSelector.
 *
 * Visibile solo a chi ha system.manage_roles NEI PERMESSI VERI: durante la
 * simulazione di un ruolo che non ce l'ha il selettore sparirebbe, e non ci
 * sarebbe piu' modo di uscire.
 */

const PERMESSO_RICHIESTO = 'system.manage_roles'
const ALTEZZA_FASCIA = 40

export const SimulazioneRuolo: React.FC = () => {
  const { token } = theme.useToken()
  const { user, permissionsReali, loading } = useAuth()
  const { ruoloSimulato, ruoli, caricamento, errore, caricaMatrice, simula, esci } = useSimulazione()
  const [pannelloAperto, setPannelloAperto] = useState(false)
  const { pathname } = useLocation()

  // Chiamata durante il render: questo componente e' montato prima delle
  // rotte in App.tsx, quindi l'azzeramento della raccolta precede le
  // registrazioni della nuova schermata. Le modali aperte dopo si sommano.
  registraPercorso(pathname)

  const abilitato = !loading && !!user && permissionsReali.includes(PERMESSO_RICHIESTO)

  // La matrice si carica una volta sola, appena il selettore diventa
  // disponibile: dentro la simulazione la sua rotta potrebbe essere preclusa.
  useEffect(() => {
    if (abilitato) void caricaMatrice()
  }, [abilitato, caricaMatrice])

  // La fascia e' in overlay: senza questo l'header dell'app le finirebbe sotto.
  useEffect(() => {
    if (!ruoloSimulato) return
    const precedente = document.body.style.paddingTop
    document.body.style.paddingTop = `${ALTEZZA_FASCIA}px`
    return () => {
      document.body.style.paddingTop = precedente
    }
  }, [ruoloSimulato])

  if (!abilitato) return null

  return (
    <>
      {ruoloSimulato && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: ALTEZZA_FASCIA,
            zIndex: 1001,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '0 16px',
            background: token.colorWarningBg,
            borderBottom: `1px solid ${token.colorWarningBorder}`,
            color: token.colorWarningText,
            fontSize: 13,
          }}
        >
          <EyeOutlined />
          <span>
            Stai vedendo come <strong>{ruoloSimulato.nome}</strong>{' '}
            <Tag style={{ fontSize: 11 }}>{ruoloSimulato.codice}</Tag> — i dati sono i tuoi, i
            permessi sono i suoi. <strong>Applicazione in sola lettura</strong>: ogni
            modifica e' bloccata finche' non esci.
          </span>
          <Button
            size="small"
            icon={<LogoutOutlined />}
            onClick={esci}
            style={{ marginLeft: 'auto' }}
          >
            Esci dalla simulazione
          </Button>
        </div>
      )}

      <div
        style={{
          position: 'fixed',
          right: 16,
          bottom: 16,
          zIndex: 1000,
          padding: '6px 10px',
          borderRadius: token.borderRadius,
          background: token.colorBgElevated,
          border: `1px solid ${token.colorBorder}`,
          boxShadow: token.boxShadowSecondary,
        }}
      >
        <Space size={8}>
          <span style={{ fontSize: 12, color: token.colorTextSecondary }}>Vedi come</span>
          <Select
            size="small"
            style={{ minWidth: 190 }}
            loading={caricamento}
            value={ruoloSimulato?.codice ?? ''}
            onChange={codice => (codice ? simula(codice) : esci())}
            options={[
              { value: '', label: 'Te stesso (nessuna simulazione)' },
              ...ruoli.map(r => ({ value: r.codice, label: `${r.nome} (${r.codice})` })),
            ]}
          />
          <Tooltip title="Permessi consultati da questa schermata">
            <Button
              size="small"
              icon={<SafetyCertificateOutlined />}
              onClick={() => setPannelloAperto(true)}
            />
          </Tooltip>
        </Space>
        {errore && (
          <Alert
            type="error"
            showIcon
            style={{ marginTop: 6, fontSize: 12 }}
            message={errore}
          />
        )}
      </div>

      <PannelloPermessi open={pannelloAperto} onClose={() => setPannelloAperto(false)} />
    </>
  )
}

export default SimulazioneRuolo
